// app/src/lib/revenue/recipes.js — recipes + plate cost (US-4.4).
//
// Plate cost = sum over ingredients of (quantity × current best unit price for item).
// "Current best unit price" = latest PriceEntry across all vendors that carry the item.

import { db } from '../db.js';
import { requireUserId, cents } from '../procurement/util.js';

/** @typedef {{ recipeId:string, userId:string, name:string, sellingPriceCents:number|null, isActive:0|1, notes?:string }} Recipe */
/** @typedef {{ ingredientId:string, userId:string, recipeId:string, itemId:string, quantity:number, uom:string }} RecipeIngredient */

export async function create(input) {
  const userId = await requireUserId();
  if (!input.name) throw new Error('recipe.create: name required');
  /** @type {Recipe} */
  const r = {
    recipeId: input.recipeId || crypto.randomUUID(),
    userId,
    name: input.name,
    sellingPriceCents: input.sellingPrice != null ? cents(input.sellingPrice) : null,
    isActive: 1,
    notes: input.notes,
  };
  await db.recipes.put(r);
  return r;
}

export async function update(recipeId, patch) {
  const userId = await requireUserId();
  const existing = await db.recipes.get(recipeId);
  if (!existing || existing.userId !== userId) throw new Error('recipe.update: not found');
  const next = {
    ...existing,
    ...patch,
    sellingPriceCents: patch.sellingPrice != null ? cents(patch.sellingPrice) : existing.sellingPriceCents,
  };
  await db.recipes.put(next);
  return next;
}

export async function deactivate(recipeId) { return update(recipeId, { isActive: 0 }); }

export async function listActive() {
  const userId = await requireUserId();
  return db.recipes.where('[userId+isActive]').equals([userId, 1]).toArray();
}

/** Add or update an ingredient line on a recipe. */
export async function setIngredient(recipeId, input) {
  const userId = await requireUserId();
  if (!input.itemId) throw new Error('recipe.setIngredient: itemId required');
  if (!Number.isFinite(input.quantity) || input.quantity <= 0) throw new Error('recipe.setIngredient: quantity > 0 required');

  // Defense-in-depth: the recipe AND the item must both belong to the current user.
  const [recipe, item] = await Promise.all([db.recipes.get(recipeId), db.items.get(input.itemId)]);
  if (!recipe || recipe.userId !== userId) throw new Error('recipe.setIngredient: recipe not found');
  if (!item || item.userId !== userId) throw new Error('recipe.setIngredient: item not found');

  const existing = await db.recipeIngredients
    .where('[recipeId+itemId]').equals([recipeId, input.itemId])
    .first();
  const row = {
    ingredientId: existing?.ingredientId || crypto.randomUUID(),
    userId,
    recipeId,
    itemId: input.itemId,
    quantity: Number(input.quantity),
    uom: input.uom || item.defaultUom || 'each',
  };
  await db.recipeIngredients.put(row);
  return row;
}

export async function removeIngredient(ingredientId) {
  const userId = await requireUserId();
  const existing = await db.recipeIngredients.get(ingredientId);
  if (!existing || existing.userId !== userId) return;
  await db.recipeIngredients.delete(ingredientId);
}

/**
 * Compute plate cost for a recipe right now.
 * @returns {Promise<{ plateCostCents:number, marginCents:number|null, marginPct:number|null, lines: Array<{ itemId:string, itemName:string, quantity:number, uom:string, unitPriceCents:number|null, totalCents:number }> }>}
 */
export async function plateCost(recipeId) {
  const userId = await requireUserId();
  const recipe = await db.recipes.get(recipeId);
  if (!recipe || recipe.userId !== userId) throw new Error('recipe.plateCost: not found');
  const ings = await db.recipeIngredients.where('recipeId').equals(recipeId).toArray();
  if (!ings.length) return { plateCostCents: 0, marginCents: null, marginPct: null, lines: [] };

  // Use preferred vendor's price when configured; fall back to lowest current price.
  const lines = await Promise.all(ings.map(async (ing) => {
    const item = await db.items.get(ing.itemId);
    const par = await db.parItems.where('[userId+itemId]').equals([userId, ing.itemId]).first();
    const allPrices = await db.priceEntries.where('itemId').equals(ing.itemId).filter((p) => p.userId === userId).toArray();
    let bestUnitCents = null;
    let basis = null;
    if (allPrices.length) {
      const latestByVendor = new Map();
      for (const p of allPrices) {
        const cur = latestByVendor.get(p.vendorId);
        if (!cur || p.effectiveDate > cur.effectiveDate) latestByVendor.set(p.vendorId, p);
      }
      if (par?.preferredVendorId && latestByVendor.has(par.preferredVendorId)) {
        const pref = latestByVendor.get(par.preferredVendorId);
        bestUnitCents = pref.unitPriceCents ?? pref.priceCents;
        basis = 'preferred-vendor';
      } else {
        const candidates = [...latestByVendor.values()].map((p) => p.unitPriceCents ?? p.priceCents);
        if (candidates.length) {
          bestUnitCents = Math.min(...candidates);
          basis = 'lowest-current';
        }
      }
    }
    const totalCents = bestUnitCents != null ? Math.round(bestUnitCents * ing.quantity) : 0;
    return {
      itemId: ing.itemId,
      itemName: item?.name || '(deleted)',
      quantity: ing.quantity,
      uom: ing.uom,
      unitPriceCents: bestUnitCents,
      basis,
      totalCents,
    };
  }));
  const plateCostCents = lines.reduce((s, l) => s + l.totalCents, 0);
  const sellingCents = recipe.sellingPriceCents;
  const marginCents = sellingCents != null ? sellingCents - plateCostCents : null;
  const marginPct = sellingCents != null && sellingCents > 0 ? (sellingCents - plateCostCents) / sellingCents : null;
  return { plateCostCents, marginCents, marginPct, lines };
}

/** Menu costing report (US-4.4 acceptance row). */
export async function costingReport() {
  const recipes = await listActive();
  const rows = await Promise.all(recipes.map(async (r) => {
    const pc = await plateCost(r.recipeId);
    return {
      recipeId: r.recipeId,
      name: r.name,
      sellingPriceCents: r.sellingPriceCents,
      plateCostCents: pc.plateCostCents,
      marginCents: pc.marginCents,
      marginPct: pc.marginPct,
    };
  }));
  rows.sort((a, b) => (b.marginPct ?? -1) - (a.marginPct ?? -1));
  return rows;
}

/**
 * Estimate plate cost impact of a single ingredient price change.
 * Used by priceAlerts when a PriceEntry write happens for an item that appears in recipes.
 * @returns {Promise<Array<{ recipeId:string, name:string, deltaPlateCostCents:number }>>}
 */
export async function recipesAffectedByItemChange(itemId, oldUnitCents, newUnitCents) {
  const userId = await requireUserId();
  if (!Number.isFinite(oldUnitCents) || !Number.isFinite(newUnitCents)) return [];
  const ings = await db.recipeIngredients.where('itemId').equals(itemId).filter((i) => i.userId === userId).toArray();
  const out = [];
  for (const ing of ings) {
    const recipe = await db.recipes.get(ing.recipeId);
    if (!recipe || recipe.isActive !== 1) continue;
    const delta = Math.round((newUnitCents - oldUnitCents) * ing.quantity);
    out.push({ recipeId: recipe.recipeId, name: recipe.name, deltaPlateCostCents: delta });
  }
  return out;
}
